import {
  X,
  User,
  Calendar,
  Phone,
  Briefcase,
  Clock,
  FileText,
  Building2,
  CheckCircle,
  AlertCircle,
  Download,
} from "lucide-react";

export const JobPopup = ({ applicationData, isOpen, onClose }) => {
  const data = applicationData;

  console.log(data, "applicationData inside model");

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleResumeDownload = async () => {
    try {
      if (data.resumeUrl) {
        const response = await fetch(data.resumeUrl);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const fileName = `${data.fullName}_Resume.pdf`;
        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      } else if (data.resumeFile) {
        const url = window.URL.createObjectURL(data.resumeFile);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${data.fullName}_Resume.pdf`);

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      } else {
        alert("No resume file available for download");
      }
    } catch (error) {
      console.error("Error downloading resume:", error);
      alert("Failed to download resume. Please try again.");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-amber-800 bg-gradient-to-br from-amber-50 to-orange-100 border-amber-200";
      case "approved":
        return "text-emerald-800 bg-gradient-to-br from-emerald-50 to-green-100 border-emerald-200";
      case "rejected":
        return "text-red-800 bg-gradient-to-br from-red-50 to-rose-100 border-red-200";
      case "closed":
        return "text-slate-800 bg-gradient-to-br from-slate-50 to-gray-100 border-slate-200";
      default:
        return "text-slate-800 bg-gradient-to-br from-slate-50 to-gray-100 border-slate-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
      case "approved":
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-slate-600" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden shadow-2xl transform scale-100 animate-in zoom-in-95 duration-300 border border-gray-100">
        {/* Compact Header */}
        <div className="relative bg-[#4F22D8]  text-white px-6 py-4 overflow-hidden">
          {/* Floating Orbs */}
          <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>

          <button
            onClick={onClose}
            className="absolute z-50 top-4 right-4 hover:bg-white/20 p-2 rounded-lg transition-all duration-200 backdrop-blur-sm border border-white/10 group"
          >
            <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
          </button>

          <div className="flex items-center relative z-10">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4 border border-white/20 shadow-lg">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl text-white font-bold mb-0 bg-gradient-to-r from-white to-blue-100 bg-clip-text">
                {data.fullName}
              </h1>
              <p className="text-blue-100/90 text-sm font-medium">
                Application Overview
              </p>
            </div>
          </div>
        </div>

        {/* Compact Content Area */}
        <div className="overflow-y-auto max-h-[calc(95vh-200px)] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="p-4">
            {/* Compact Status Cards */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div
                className={`rounded-xl p-4 border ${getStatusColor(
                  data.status
                )} shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-lg bg-white/50 backdrop-blur-sm mr-3">
                    {getStatusIcon(data.status)}
                  </div>
                  <div>
                    <p className="text-xs font-semibold opacity-70 uppercase tracking-wider mb-0">
                      Status
                    </p>
                    <p className="text-xl font-bold capitalize">
                      {data.status}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl p-4 border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg bg-white/80 backdrop-blur-sm mr-3">
                    <Calendar className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-0">
                      Applied On
                    </p>
                    <p className="text-xl font-bold text-slate-900">
                      {formatDate(data.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Compact Information Grid */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Compact Personal Information */}
              <div className="space-y-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 flex justify-center items-center rounded-lg bg-blue-100 mr-3">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Personal Information
                  </h2>
                </div>

                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
                  {[
                    { label: "Full Name", value: data.fullName },
                    {
                      label: "Date of Birth",
                      value: formatDate(data.birthDate),
                    },
                    { label: "Gender", value: data.gender },
                    { label: "City", value: data.city },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-2 border-b border-gray-50 last:border-b-0 group hover:bg-gray-25 rounded-lg px-1 transition-colors duration-200"
                    >
                      <span className="text-gray-600 font-medium text-sm">
                        {item.label}
                      </span>
                      <span className="text-gray-900 font-semibold text-sm group-hover:text-blue-600 transition-colors duration-200">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Compact Contact Information */}
                <div>
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 flex justify-center items-center rounded-lg bg-green mr-3">
                      <Phone className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Contact Information
                    </h2>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
                    {[
                      {
                        label: "Phone",
                        value: data.phone,
                        color: "text-gray-900",
                      },
                      {
                        label: "Email",
                        value: data.email,
                        color: "text-blue-600",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center py-2 border-b border-gray-50 last:border-b-0 group hover:bg-gray-25 rounded-lg px-1 transition-colors duration-200"
                      >
                        <span className="text-gray-600 font-medium text-sm">
                          {item.label}
                        </span>
                        <span
                          className={`${item.color} font-semibold text-sm group-hover:scale-105 transition-transform duration-200`}
                        >
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Compact Professional Information */}
              <div className="space-y-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 flex justify-center items-center rounded-lg bg-purple-100 mr-3">
                    <Briefcase className="w-4 h-4 text-purple-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Professional Information
                  </h2>
                </div>

                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
                  {[
                    {
                      label: "Highest Qualification",
                      value: data.highestQualification,
                    },
                    { label: "Graduation Year", value: data.yearOfGraduation },
                    { label: "Current Role", value: data.jobTitle },
                    { label: "Experience", value: `${data.experience} years` },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-2 border-b border-gray-50 last:border-b-0 group hover:bg-gray-25 rounded-lg px-1 transition-colors duration-200"
                    >
                      <span className="text-gray-600 font-medium text-sm">
                        {item.label}
                      </span>
                      <span className="text-gray-900 font-semibold text-right max-w-40 text-sm group-hover:text-purple-600 transition-colors duration-200">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Compact Job Information */}
                <div>
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 flex justify-center items-center rounded-lg bg-indigo-100 mr-3">
                      <Building2 className="w-4 h-4 text-indigo-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Job Information
                    </h2>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
                    <div className="flex flex-col items-start space-y-2 py-2 border-b border-gray-50 group hover:bg-gray-25 rounded-lg px-1 transition-colors duration-200">
                      <span className="text-gray-600 font-medium text-sm">
                        Position
                      </span>
                      <span className="text-gray-900 font-semibold text-sm group-hover:text-indigo-600 transition-colors duration-200">
                        {data.jobId.title}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-50 group hover:bg-gray-25 rounded-lg px-1 transition-colors duration-200">
                      <span className="text-gray-600 font-medium text-sm">
                        Location
                      </span>
                      <span className="text-gray-900 font-semibold text-sm group-hover:text-indigo-600 transition-colors duration-200">
                        {data.jobId.location}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 group hover:bg-gray-25 rounded-lg px-1 transition-colors duration-200">
                      <span className="text-gray-600 font-medium text-sm">
                        Job Status
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold capitalize border ${getStatusColor(
                          data.jobId.status
                        )} transform group-hover:scale-105 transition-all duration-200`}
                      >
                        {data.jobId.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Compact Additional Information */}
            <div className="grid lg:grid-cols-3 gap-4 mt-6">
              {/* Updated Resume Card with Download Functionality */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-4 border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 flex justify-center items-center rounded-lg bg-white/80 mr-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">Resume</h3>
                </div>
                <button
                  onClick={handleResumeDownload}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 text-sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Resume
                </button>
              </div>

              {/* Compact Timeline Card */}
              <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl p-4 lg:col-span-2 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 flex justify-center items-center rounded-lg bg-white/80 mr-2">
                    <Clock className="w-4 h-4 text-slate-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">Timeline</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { label: "Applied On", value: formatDate(data.createdAt) },
                    {
                      label: "Last Updated",
                      value: formatDate(data.updatedAt),
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="bg-white/60 backdrop-blur-sm p-3 rounded-lg border border-white/50"
                    >
                      <p className="text-xs text-gray-600 mb-1 font-medium">
                        {item.label}
                      </p>
                      <p className="font-bold text-gray-900 text-sm">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Compact Declaration */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-100 border border-emerald-200 rounded-xl p-4 mt-6 shadow-lg">
              <div className="flex items-center">
                <div className="p-1.5 rounded-lg bg-white/80 mr-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-emerald-800 text-sm mb-0">
                    Declaration Verified
                  </h3>
                  <p className="text-emerald-700 font-medium text-xs">
                    All information provided has been confirmed by the applicant
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compact Footer */}
        <div className="bg-gradient-to-r from-gray-50 to-slate-100 px-6 py-4 border-t border-gray-200/70 flex justify-between items-center backdrop-blur-sm">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 font-semibold hover:text-gray-900 transition-all duration-200 rounded-lg border border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 text-sm"
            >
              Close
            </button>
            <button className="bg-[#4F22D8]  text-white px-8 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 text-sm">
              Take Action
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
